import React, { useState, useEffect } from "react";
import "./edit-building.component.scoped.sass";
import {
  createStyles,
  Theme,
  withStyles,
  WithStyles,
} from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import MuiDialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import {
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import { BuildingsService } from "src/services/buildings/buildings.service";
import { useTranslation } from "react-i18next";
import { icons } from "src/statics/icons";
import { BuildingInput } from "src/types/buildings-input";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: "absolute",
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500],
    },
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions);

export default function EditBuildingDialog(props: any) {
  const [t] = useTranslation();
  const emptyObj: any = {};
  const buildingsService = new BuildingsService();
  const [loading, setLoading]: any = useState(false);
  const [hasError, setFormError]: any = useState(false);
  const [building, setBuilding]: any = useState(emptyObj);
  const [country, setCountry] = useState("");
  const { createBuildingsIco } = icons;

  const errorFinder = (building: BuildingInput) => {
    let hasError = false;
    for (const i in building) {
      if(!building[i]) {
        hasError = true;
      }
    }
    return hasError;
  }

  useEffect(() => {
    setBuilding(props.data);
    setCountry(props.data.country);
    // eslint-disable-next-line
  }, [props.data]);

  const handleClose = (save: boolean = false) => {
    save ? props.onClose(building) : props.onClose();
  };

  const saveBuilding = async(): Promise<void> => {
    setLoading(true);
    await buildingsService.updateBuilding(building);
    setLoading(false);
    handleClose(true);
  };

  const inputChangeHandler = (event: any, prop: string) => {
    const value = event?.target?.value;
    const obj: any = {};
    obj[prop] = value;
    setBuilding({...building, ...obj})
    setFormError(errorFinder({...building, ...obj}))
    if (prop === "country") {
      setCountry(value);
    }
  };

  return (
    <Dialog
      disableBackdropClick
      onClose={handleClose.bind(null, false)}
      aria-labelledby="customized-dialog-title"
      open={props.open}
      fullWidth={true}
    >
      <DialogTitle id="customized-dialog-title" onClose={handleClose.bind(null, false)}>
        {t('edit') + ' - ' + building.name}
      </DialogTitle>
      <DialogContent dividers>
        <div className="one-building">
          <TextField
            name="tagName"
            type="text"
            className="bill-input"
            label={t("tag_name")}
            onChange={(event: any) => inputChangeHandler(event, "name")}
            value={building.name} 
            InputLabelProps={{ shrink: building.name ? true : false }}
          />
          <Grid container direction="row" justify="space-between">
            <Grid item xs={3} sm={3}>
              <FormControl
                style={{ width: "calc(100% - 10px)" }}
                className="bill-input country-filter"
              >
                <InputLabel id="country">{t("country")}</InputLabel>
                <Select
                  labelId="country"
                  id="country-select"
                  onChange={(event: any) =>
                    inputChangeHandler(event, "country")
                  }
                  value={country}
                >
                  <MenuItem value={"Austria"}>Austria</MenuItem>
                  <MenuItem value={"Egypt"}>Egypt</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4} sm={4}>
              <TextField
                name="postalCode"
                type="number"
                className="bill-input"
                style={{ width: "calc(100% - 30px)" }}
                label={t("postalCode")}
                onChange={(event: any) =>
                  inputChangeHandler(event, "postalCode")
                }
                value={building.postalCode} 
                InputLabelProps={{ shrink: building.postalCode ? true : false }}
              />
            </Grid>
            <Grid item xs={5} sm={5}>
              <TextField
                name="city"
                type="text"
                className="bill-input"
                style={{ width: "calc(100% - 17px)" }}
                label={t("city")}
                onChange={(event: any) =>
                  inputChangeHandler(event, "city")
                }
                value={building.city} 
                InputLabelProps={{ shrink: building.city ? true : false }}
              />
            </Grid>
          </Grid>
          <TextField
            name="address"
            type="text"
            className="bill-input"
            label={t("address")}
            onChange={(event: any) =>
              inputChangeHandler(event, "address")
            }
            value={building.address} 
            InputLabelProps={{ shrink: building.address ? true : false }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          variant="contained"
          disabled={loading || hasError}
          className="btn-tool btn-warning"
          onClick={saveBuilding}
        >
          {!loading && <div className="btn-flex"><img
            alt="icon"
            className="icon icon-refresh"
            src={createBuildingsIco}
          />{t("save")}</div>}
          {loading && <CircularProgress color="primary" />}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
